import React, { FC, useContext, useState } from "react";
import styled, { css } from "styled-components";
import Link from "next/link";
import Sticky from "react-stickynode";

import CheckoutContext from "checkout";
import { getUndef, getIngredients } from "checkout/utils";
import { GRAY, GRAY_LIGHT } from "../styles/constants";
import Counter from "./Counter";
import { SIDE_SPACING } from "../styles/max-width";
import Promocode from "./Promocode";
import Button from "./Button";
import useWorkingHours from "../hooks/use-working-hours";
import {
  ecommerceAddToBasket,
  ecommerceRemoveFromBasket,
} from "../../lib/ym-ecommerce";
import WithdrawBonuses from "../bonus/WIthdrawBonuses";
import CartBonuses from "../bonus/CartBonuses";
import BonusAuth from "../bonus/BonusAuth";
import AuthorizationContext from "../context/Authorization";
import Loader from "./Loader";
import useShop from "../hooks/use-shop";

interface Props {
  title: string;
  description?: string;
  fullOnWidth?: number;
  order?: boolean;
}

const Cart: FC<Props> = ({
  title,
  description,
  fullOnWidth,
  order,
  ...props
}) => {
  const {
    checkout,
    addToCart,
    removeFromCart,
    optimisticLineItems,
    ingredientIds,
    bonuses,
    setBonuses,
  } = useContext(CheckoutContext);

  const { isAuthorized, balance, balanceLoading, card } =
    useContext(AuthorizationContext);

  const { isClosed, closedText } = useWorkingHours();
  const { primaryDomain } = useShop();

  const totalPrice = getUndef(() => checkout.totalPrice);
  const subtotalPrice = getUndef(() => checkout.subtotalPrice);
  const totalBonus = getUndef(() => checkout.iikoBonusesAdd);
  const shippingPrice = getUndef(() => checkout.shippingLine.price);
  const shippingAddress = getUndef(() => checkout.shippingAddress.address1);

  const freeShipping = shippingPrice === 0 && !!shippingAddress;

  const resultShippingPrice = freeShipping ? 0 : shippingPrice;
  const connected = !!card;
  const priceWithDelivery = totalPrice + resultShippingPrice;

  return (
    <Wrapper id="sticky-parent" {...props}>
      <Sticky
        top={90}
        topBoundary="#sticky-parent"
        bottomBoundary="#sticky-parent"
      >
        <StickyContent>
          <Block fullOnWidth={fullOnWidth}>
            <Top>
              <h5>{title}</h5>
              {description && <p className="description">{description}</p>}
            </Top>
            <List>
              {optimisticLineItems
                .filter(({ variant: { id } }) => {
                  return !ingredientIds.find(
                    (ingredientId) => ingredientId === id
                  );
                })
                .map(
                  (
                    {
                      customAttributes,
                      expectedQuantity,
                      expectedPrice,
                      priceCompareAt,
                      variant: {
                        id,
                        name: variantName,
                        product: { name, slug, collections },
                      },
                    },
                    index
                  ) => {
                    const maybeDeleted = expectedQuantity <= 0;
                    if (maybeDeleted) {
                      return false;
                    }

                    const ingredients = getIngredients(
                      customAttributes.filter(
                        (attr) => attr.key === "ingredient"
                      )
                    );

                    const ingredientsString = ingredients.map(
                      (ingredient, index) =>
                        ingredient.name.toLowerCase() +
                        (index + 1 < ingredients.length ? ", " : "")
                    );

                    const slugCollection = getUndef(() => collections[0].slug);

                    const href = `/catalog/${slugCollection}/${slug}`;

                    return (
                      <Item key={index}>
                        <div className="left">
                          <Link href={`/catalog/[code]/[item]`} as={href}>
                            <a href={href} className="name">
                              {name}
                            </a>
                          </Link>
                          {ingredientsString.length ? (
                            <p className="params">{ingredientsString}</p>
                          ) : null}
                          {variantName !== name && (
                            <p className="params">{variantName}</p>
                          )}
                        </div>
                        <div className="right">
                          <div className="price-wrap">
                            {priceCompareAt && (
                              <span className="prev-price">
                                {priceCompareAt} ₽
                              </span>
                            )}
                            {/* <span className="price free">Бесплатно</span> */}
                            <span className="price">{expectedPrice} ₽</span>
                          </div>
                          <Counter
                            value={expectedQuantity}
                            add={() => {
                              addToCart({ id, ingredients });
                              try {
                                ecommerceAddToBasket({
                                  id,
                                  name,
                                  price: expectedPrice,
                                  category: slugCollection,
                                  quantity: expectedQuantity,
                                });
                              } catch (error) {
                                console.error(error);
                              }
                            }}
                            remove={() => {
                              removeFromCart({ id, ingredients });
                              try {
                                ecommerceRemoveFromBasket({
                                  id,
                                  name,
                                  category: slugCollection,
                                  quantity: expectedQuantity,
                                });
                              } catch (error) {
                                console.error(error);
                              }
                            }}
                          />
                        </div>
                      </Item>
                    );
                  }
                )}
            </List>

            {totalPrice && optimisticLineItems.length ? (
              <>
                {shippingPrice || freeShipping ? (
                  <Line>
                    <span className="name">Доставка</span>
                    <span className="line" />
                    <span className={`value${freeShipping ? " free" : ""}`}>
                      {freeShipping ? "Бесплатно" : `${shippingPrice} ₽`}
                    </span>
                  </Line>
                ) : null}
                {Boolean(subtotalPrice - (totalPrice - bonuses)) && (
                  <Line>
                    <span className="name">Скидка</span>
                    <span className="line" />
                    <span className="value">
                      {subtotalPrice - (totalPrice - bonuses)} ₽
                    </span>
                  </Line>
                )}
                <Total>
                  <span className="label">Итого</span>
                  <span className="content">
                    {subtotalPrice !== totalPrice && (
                      <span className="prev-price">
                        {subtotalPrice + resultShippingPrice} ₽
                      </span>
                    )}
                    <span className="price h3">
                      {priceWithDelivery - bonuses} ₽
                    </span>
                  </span>
                </Total>
                {!order && (
                  <Link href="/order">
                    <OrderLink href="/order">
                      <Button
                        type="button"
                        primary
                        full
                        disabled={isClosed || primaryDomain?.holydays}
                      >
                        {isClosed ? closedText : "Оформить заказ"}
                      </Button>
                    </OrderLink>
                  </Link>
                )}
              </>
            ) : (
              <EmptyBaket>
                <Separator />
                <img src="../../static/icons/shopping-bag.svg" alt="корзина" />
                <TextBig>Ваша корзина пуста</TextBig>
                <TextSmall>Но это можно исправить прямо сейчас!</TextSmall>
              </EmptyBaket>
            )}
          </Block>

          {order && connected ? (
            <BonusBlock>
              {isAuthorized ? (
                balanceLoading ? (
                  <BonusLoader />
                ) : (
                  <WithdrawBonuses
                    value={bonuses}
                    setValue={setBonuses}
                    balance={balance}
                    total={Math.min(balance, totalPrice)}
                  />
                )
              ) : (
                <BonusAuth />
              )}
            </BonusBlock>
          ) : null}

          {totalBonus !== null &&
            totalBonus !== undefined &&
            +totalBonus > 0 && (
              <CartBonus>
                <BonusRow>
                  <BonusText>
                    Начислится бонусов на&nbsp;бонусный счет
                  </BonusText>
                  <BonusValue $disabled={!!bonuses}>
                    <span>+{bonuses ? 0 : totalBonus} ₽</span>
                    <Icon
                      style={{
                        backgroundImage: `url('/static/icons/bonus-${
                          bonuses ? "gray" : connected ? "green" : "red"
                        }.svg')`,
                      }}
                    />
                  </BonusValue>
                </BonusRow>
                <BonusRules href="/bonus-program" target="_blank">
                  Правила начисления бонусов
                </BonusRules>
              </CartBonus>
            )}

          <CartPromocode />

          {!order && (
            <Block>
              {isAuthorized ? (
                <CartBonuses connected={connected} total={balance} />
              ) : (
                <BonusAuth />
              )}
            </Block>
          )}
        </StickyContent>
      </Sticky>
    </Wrapper>
  );
};

const CartPromocode = styled(Promocode)`
  margin-bottom: 20px;
  @media (max-width: 600px) {
    margin: 0 1rem;
  }
`;

const Wrapper = styled.div``;

const StickyContent = styled.div`
  /* padding-bottom: 20px; */
`;

const EmptyBaket = styled.div`
  text-align: center;

  img {
    margin-top: 20px;
  }
`;

const TextBig = styled.p`
  opacity: 0.5;
  font-size: 16px;
`;
const TextSmall = styled.p`
  opacity: 0.5;
  font-size: 12px;
`;

const Separator = styled.hr`
  display: block;
  margin: 0;
  height: 1px;
  background: none;
  border: none;
  border-bottom: 1px solid ${GRAY_LIGHT};
`;

interface BlockProps {
  fullOnWidth?: number;
}
const Block = styled.div<BlockProps>`
  background-color: #fff;
  border-radius: 10px;
  padding: 20px;
  &:not(:last-child) {
    margin-bottom: 20px;
  }

  &: ${({ fullOnWidth }) =>
    fullOnWidth
      ? css`
          & {
            @media (max-width: ${fullOnWidth}px) {
              border-radius: 0;
              padding-left: ${SIDE_SPACING}%;
              padding-right: ${SIDE_SPACING}%;
            }
          }
        `
      : undefined};
`;

const BonusBlock = styled(Block)`
  position: relative;
  min-height: 123px;
`;

const BonusLoader = styled(Loader)`
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  margin: auto;
`;

const Top = styled.div`
  padding-bottom: 20px;
  text-align: center;

  h5 {
    text-transform: uppercase;
  }

  .description {
    font-size: 12px;
    color: ${GRAY};
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Item = styled.li`
  display: flex;
  padding: 1em 0;
  border-bottom: 1px solid ${GRAY_LIGHT};
  &:first-child {
    border-top: 1px solid ${GRAY_LIGHT};
  }

  .left {
    flex-grow: 1;
    margin-right: 15px;
  }

  .right {
    flex-shrink: 0;
    text-align: right;
  }

  .name {
    line-height: 18px;
    margin-bottom: 0.5em;
    display: inline-block;
  }

  .params {
    font-size: 12px;
    color: ${GRAY};
  }

  .price {
    display: inline-block;
    text-align: right;
    font-size: 20px;

    &.free {
      font-size: 15px;
    }
  }

  .price-wrap {
    display: flex;
    justify-content: flex-end;
    line-height: 1;
    margin-bottom: 0.5em;
  }

  .prev-price {
    display: inline-block;
    color: ${GRAY};
    text-decoration: line-through;
    margin-right: 0.5em;
  }
`;

const Total = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 1em 0;

  & > span {
    display: inline-block;
  }

  .label {
    color: ${GRAY};
  }

  .prev-price,
  .price {
    display: block;
    text-align: right;
    line-height: 1;
  }

  .prev-price {
    color: ${GRAY};
    text-decoration: line-through;
    font-size: 16px;
    margin-bottom: 10px;
    padding-top: 5px;
  }
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin: 20px 0;

  .name,
  .value,
  .line {
    display: inline-block;
  }

  .name,
  .value {
    flex-shrink: 0;
  }

  .name {
    color: ${GRAY};
  }

  .value {
    font-size: 18px;
    line-height: 1.4;

    &.free {
      font-size: 15px;
    }
  }

  .line {
    flex-grow: 1;
    border-bottom: 1px dashed ${GRAY_LIGHT};
    margin: 0 0.4em 0.45em;
  }
`;

const OrderLink = styled.a`
  display: block;
`;

const CartBonus = styled(Block)`
  padding: 20px;
`;

const BonusRow = styled.div`
  display: flex;
  align-items: center;
`;

const BonusText = styled.span`
  margin-right: 20px;
  font-size: 12px;
  line-height: 1.2;
  color: ${GRAY};
`;

interface BonusValue {
  $disabled: boolean;
}
const BonusValue = styled.span<BonusValue>`
  display: inline-flex;
  align-items: center;
  flex-basis: 70px;
  flex-shrink: 0;
  text-align: right;
  font-size: 20px;
  white-space: nowrap;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const Icon = styled.span`
  display: inline-block;
  width: 31px;
  height: 22px;
  margin-left: 9px;
  background-size: contain;
  background-repeat: no-repeat;
`;

const BonusRules = styled.a`
  font-size: 12px;
  color: #78b833;
`;

export default Cart;
